import isEmpty from 'lodash/isEmpty';
import { stringify } from 'qs';
import * as apiTypes from '../../constants/Api';
import {
  activationDateGetAction,
  createUpdateAccidentAction,
  createUpdateCarAction,
  createUpdateContentAction,
  createUpdateHouseAction,
  createUpdatePersonalAction,
  createUpdateTravelAction,
  createUpdateCustomerAction,
  quoteCustomerAction,
  updateQuoteAction,
  prepareAccidentAction,
  prepareCarAction,
  prepareContentAction,
  prepareHouseAction,
  preparePersonalAction,
  prepareTravelAction,
  purchaseAction,
  validateCarRegistrationNumberAction,
  saveOfferAction,
  retrieveOfferAction,
  createInsuranceCheckAction,
  sendRecommendationAction,
  createUpdateDogAction,
  prepareDogAction,
  createUpdateCatAction,
  prepareCatAction,
  portratiLeadAction,
} from '../../actions/Api';
import { userDataUpdateLoaderAction, userDataResetLoaderAction } from '../../actions/UserData';
import { appLoadingAction } from '../../actions/App';
import { requestCredentials } from './auth';
import { getCarExcessAmount } from './car';
import { getContentExcessAmount } from './content';
import { getHouseExcessAmount } from './house';
import { getTravelExcessAmount } from './travel';
import { getActivationDates } from './customer';
import { replaceAll, resolvePath } from '../common';
import { getDogExcessAmount } from './dog';
import { getCatExcessAmount } from './cat';

/**
 * Switches keys around based on the Dawa documentation.
 * @param  {Object} params Params to process.
 * @param  {Object} map    Map of param keys to switch.
 * @return {Object}        New params with correct keys.
 */
export const formatParams = (params, map) => {
  const newParams = params;
  map.forEach(keyMap => {
    if (Object.hasOwnProperty.call(newParams, keyMap.name)) {
      Object.defineProperty(
        newParams,
        keyMap.mappedName,
        Object.getOwnPropertyDescriptor(newParams, keyMap.name)
      );
      delete newParams[keyMap.name];
    }
  });
  return newParams;
};

/**
 * Build a URL with an optional query string.
 * @param  {string} endpoint    The base URL.
 * @param  {Object} [params={}] Object containing the params for the query string.
 * @return {string}             The generated URL.
 */
export const buildUrl = (endpoint, params = {}) => {
  const esc = encodeURIComponent;

  if (!isEmpty(params)) {
    const query = Object.keys(params)
      .map(k => `${esc(k)}=${esc(params[k])}`)
      .join('&');
    return `${endpoint}?${query}`;
  }
  return `${endpoint}`;
};

function buildHeaders({ authorization, isGet, ctForm }) {
  const headers = {};

  if (authorization) {
    headers.Authorization = authorization;
  }

  if (!isGet) {
    headers['Content-Type'] = ctForm
      ? 'application/x-www-form-urlencoded'
      : 'application/json';
  }

  return headers;
}

export const callApi = (
  method, url, params, dispatch, externalService, ctForm = false, token = null, basicAuth = null
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 300000);

  const isGet = method === 'get';
  const isBodyMethod = method === 'post' || method === 'patch';

  let requestUrl = url;
  let body;

  if (isGet && params) {
    requestUrl += `?${new URLSearchParams(params)}`;
  }

  if (isBodyMethod && params) {
    body = ctForm ? stringify(params) : JSON.stringify(params);
  }

  if (token) {
    requestUrl = `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}access_token=${token.access_token}`;
  }

  function createBasicAuthHeader({ user, password }) {
    return `Basic ${btoa([user, password].join(':'))}`;
  }

  const authorization =
    !externalService && basicAuth
      ? createBasicAuthHeader(basicAuth)
      : undefined;

  const headers = buildHeaders({
    authorization,
    isGet,
    ctForm,
  });

  return fetch(requestUrl, {
    method: method.toUpperCase(),
    headers,
    body,
    signal: controller.signal,
  })
    .then(response => {
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        return response.json().then(parsedBody => ({
          data: parsedBody,
          status: response.status,
          headers: response.headers,
        }));
      }

      return {
        data: undefined,
        status: response.status,
        headers: response.headers,
      };
    })
    .finally(() => clearTimeout(timeoutId));
};


const callApiWithoutToken = (method, url, params, dispatch, externalService, ctForm = false) => {
  // Fetch our credentials.
  return dispatch(requestCredentials(true)).then(credentials => {
    const { basicAuth } = credentials;
    return callApi(method, url, params, dispatch, externalService, ctForm, null, basicAuth)
      .then(data => {
        if (params.responseAsIs) {
          return data.data;
        }

        return {
          ...data.data,
          usedParams: params,
        };
      });
  });
};

const callApiWithToken = (method, url, params, dispatch, externalService, ctForm = false) => {
  // Fetch our credentials.
  return dispatch(requestCredentials()).then(credentials => {
    const { token, basicAuth } = credentials;
    return callApi(method, url, params, dispatch, externalService, ctForm, token, basicAuth)
      .then(data => {
        if (params.responseAsIs) {
          return data.data;
        }

        return {
          ...data.data,
          usedParams: params,
        };
      });
  });
};

/**
 * Call a URL using GET. Params will be added as query string.
 * @param  {string} url    The enpoint.
 * @param  {object} params Params to add as query string.
 * @param  {boolean} externalService Set to true if this is a call to an external service.
 * @return {Promise}        The promise.
 */
export const callApiGet = (url, params, dispatch, externalService = false, useToken = true) => {
  if (useToken) {
    return callApiWithToken('get', url, params, dispatch, externalService);
  }
  return callApiWithoutToken('get', url, params, dispatch, externalService);
};

/**
 * Call a URL using POST. Data will be added as request data.
 * @param  {string} url    The enpoint.
 * @param  {object} data   Params to add as data.
 * @param  {boolean} externalService Set to true if this is a call to an external service.
 * @return {Promise}       The promise.
 */
export const callApiPost = (
  url,
  data,
  dispatch,
  externalService = false,
  ctForm = false,
  useToken = true
) => {
  if (useToken) {
    return callApiWithToken('post', url, data, dispatch, externalService, ctForm);
  }
  return callApiWithoutToken('post', url, data, dispatch, externalService, ctForm);
};

/**
 * Call a URL using PATCH. Data will be added as request data.
 * @param  {string} url    The enpoint.
 * @param  {object} data   Params to add as data.
 * @param  {boolean} externalService Set to true if this is a call to an external service.
 * @return {Promise}       The promise.
 */
export const callApiPatch = (url, data, dispatch, externalService = false, useToken = true) => {
  if (useToken) {
    return callApiWithToken('patch', url, data, dispatch, externalService);
  }
  return callApiWithoutToken('patch', url, data, dispatch, externalService);
};

/**
 * Contructs the correct API url.
 * @param {string} type             The type of service to call.
 * @param {string} service          The service to use.
 * @param {string} serviceEndPoint  Service path.
 * @return {string}                 The url.
 */
export const constructApiUrl = (type, service, serviceEndPoint) => {
  const serviceUrl = serviceEndPoint || 'calcbuy';
  if (!type) {
    return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${service}`;
  }
  if (!service) {
    return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${type}`;
  }
  return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${type}/${service}`;
};

/**
 * Function that maps the params needed with their values.
 * @param  {array}  params     Array of param fields needed in the service. Defined in the JSON.
 * @param  {Object} formValues The current form values.
 * @param  {Object} state      The current state
 * @param  {string} product    If we need to fetch product specific values.
 * @return {Object}            Object containing the needed params for the service.
 */
export const createParams = (params, formValues, state, product = null, extraData = {}) => {
  // Merge state and formValues. Form can override state.
  let values = {
    ...state.userData,
    ...formValues,
    ...extraData,
  };

  if (product && state.userData[product]) {
    values = {
      ...values,
      ...state.userData[product],
    };
  }
  // Now map the values to their respective keys.
  const mappedParams = params.reduce((result, item) => {
    const newResult = result;
    let key = item.valueFrom ? item.valueFrom : item.field;
    key = replaceAll(key, state.section.packageData.translations, values);
    if (Object.hasOwnProperty.call(item, 'value')) {
      newResult[item.field] = item.value;
    } else if (Object.hasOwnProperty.call(values, key)) {
      if (values[key] === false || values[key] || values[key] === '') {
        newResult[item.field] = values[key];
      }
    } else {
      const value = resolvePath(values, key);
      if (value !== undefined) {
        newResult[item.field] = value;
      }
    }
    return newResult;
  }, {});
  return mappedParams;
};

/**
 * Function that ensures that parameters are set as defined in the service.
 * @param  {Object} mappedParams  The parameter object to validate.
 * @param  {array}  serviceParams The params defined in the JSON config.
 * @return {array}                Array of errors found.
 */
export const validateParams = (mappedParams, serviceParams) => {
  const errors = serviceParams.reduce((result, param) => {
    let curErrors = result;
    if (param.required &&
      (!Object.hasOwnProperty.call(mappedParams, param.field))) {
      curErrors = [...result, param.field];
    }
    return curErrors;
  }, []);
  return errors;
};

/**
 * Function that return an object containing params that are either not in the prevParams or that
 * are updated.
 * @param  {Object} curParams         The params being used for current request.
 * @param  {Object} prevParams        Params used at a previous request.
 * @param  {Array}  persist           Array of fields to persist in the params.
 * @param  {Object} additionalParams  Array of fields to persist in the params.
 * @return {Object}                   The udpated params.
 */
export const getChangedParams = (curParams, prevParams, persist = [], additionalParams = {}) => {
  const returnParams = Object.keys(curParams).reduce((result, key) => {
    const curResult = result;
    // If we should persist this param just return it.
    // If no params has been set earlier we use it.
    // If the two params are different we should update the params.
    if (prevParams && (!Object.hasOwnProperty.call(prevParams, key) ||
      persist.includes(key) ||
      curParams[key] !== prevParams[key]
    )) {
      curResult[key] = curParams[key];
      return curResult;
    }

    return curResult;
  }, {});
  return {
    ...returnParams,
    ...additionalParams,
  };
};

/**
 * Gets the basic auth credentials from the state.
 * @param  {Object} state    The current state
 * @return {Object|undefied} The basic auth
 */
export const getBasicAuth = state => {
  const {
    section: {
      packageData: {
        credentials,
      },
    },
  } = state;
  if (!credentials) {
    return {
      user: process.env.REACT_APP_UAT_USER,
      password: process.env.REACT_APP_UAT_PASS,
    };
  }

  const { basicAuth } = credentials;
  return basicAuth;
};

/**
 * Handler for calling services supported.
 * @param  {string}   service     The service to call.
 * @param  {Object}   params      Params needed in the service.
 * @param  {string}   forProduct  Only run if the product is in the basketlist.
 * @param  {Object}   formValues  The current forms values.
 * @param  {Function} dispatch    Dispatch function from the store.
 * @return {Promise}              The promise from the called service.
 */
export const callService = (service, params, forProduct, formValues, dispatch) => {
  if (forProduct && !formValues.basketList.includes(forProduct)) {
    return Promise.resolve();
  }
  switch (service) {
    case 'accidentCreateUpdate':
      return dispatch(createUpdateAccidentAction(params, formValues));
    case 'accidentUpdateQuote':
      return dispatch(updateQuoteAction(params, 'accident', formValues));
    case 'accidentPreparePurchase':
      return dispatch(prepareAccidentAction(params, formValues));
    case 'carCreateUpdate': {
      const {
        make,
        model,
        modelYear,
        registrationNumber,
        vehicleMakeId,
        registrationNumberProvided,
      } = formValues;
      return dispatch(createUpdateCarAction(params, {
        ...formValues,
        make: make || '',
        model: model || '',
        modelYear: modelYear || '',
        registrationNumberProvided: !!registrationNumberProvided,
        vehicleMakeId: vehicleMakeId || '',
        ...registrationNumber ? {
          registrationNumber: registrationNumber.replace(/\s/g, '').toUpperCase(),
        } : {},
      }));
    }
    case 'carExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getCarExcessAmount(formValues, dispatch);
    case 'carVehicleModelsByRegistrationNumber':
      return dispatch(validateCarRegistrationNumberAction({
        registrationNumber:
          formValues.registrationNumber ?
            formValues.registrationNumber.replace(/\s/g, '').toUpperCase() :
            '',
      }, formValues));
    case 'carUpdateQuote':
      return dispatch(updateQuoteAction(params, 'car', formValues));
    case 'carPreparePurchase':
      return dispatch(prepareCarAction(params, formValues));
    case 'contentCreateUpdate':
      return dispatch(createUpdateContentAction(params, formValues));
    case 'contentExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getContentExcessAmount(formValues, dispatch);
    case 'contentUpdateQuote':
      return dispatch(updateQuoteAction(params, 'content', formValues));
    case 'contentPreparePurchase':
      return dispatch(prepareContentAction(params, formValues));
    case 'customerCreateUpdate':
      return dispatch(createUpdateCustomerAction(params, formValues));
    case 'customerQuote':
      return dispatch(quoteCustomerAction());
    case 'customerUpdateQuote':
      return dispatch(updateQuoteAction(params, null, formValues));
    case 'getActivationDate':
      return dispatch(activationDateGetAction(formValues));
    case 'getMinActivationDate':
      return dispatch(activationDateGetAction(formValues, true));
    case 'getActivationDates':
      return getActivationDates(formValues, dispatch);
    case 'houseCreateUpdate':
      return dispatch(createUpdateHouseAction(params, formValues));
    case 'houseExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getHouseExcessAmount(formValues, dispatch);
    case 'travelExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getTravelExcessAmount(formValues, dispatch);
    case 'houseUpdateQuote':
      return dispatch(updateQuoteAction(params, 'house', formValues));
    case 'housePreparePurchase':
      return dispatch(prepareHouseAction(params, formValues));
    case 'personalCreateUpdate':
      return dispatch(createUpdatePersonalAction(params, formValues));
    case 'personalUpdateQuote':
      return dispatch(updateQuoteAction(params, 'personal', formValues));
    case 'personalPreparePurchase':
      return dispatch(preparePersonalAction(params, formValues));
    case 'purchase':
      return dispatch(purchaseAction(params, formValues));
    case 'travelCreateUpdate':
      return dispatch(createUpdateTravelAction(params, formValues));
    case 'travelUpdateQuote':
      return dispatch(updateQuoteAction(params, 'travel', formValues));
    case 'travelPreparePurchase':
      return dispatch(prepareTravelAction(params, formValues));
    case 'resetLoader':
      return dispatch(userDataResetLoaderAction());
    case 'saveOffer':
      return dispatch(saveOfferAction(params, formValues));
    case 'retrieveOffer':
      return dispatch(retrieveOfferAction(params, formValues));
    case 'insuranceCheck':
      return dispatch(createInsuranceCheckAction(params, formValues));
    case 'sendRecommendation':
      return dispatch(sendRecommendationAction(params, formValues));
    case 'dogCreateUpdate':
      return dispatch(createUpdateDogAction(params, formValues));
    case 'dogUpdateQuote':
      return dispatch(updateQuoteAction(params, 'dog', formValues));
    case 'dogPreparePurchase':
      return dispatch(prepareDogAction(params, formValues));
    case 'dogExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getDogExcessAmount(formValues, dispatch);
    case 'catCreateUpdate':
      return dispatch(createUpdateCatAction(params, formValues));
    case 'catUpdateQuote':
      return dispatch(updateQuoteAction(params, 'cat', formValues));
    case 'catPreparePurchase':
      return dispatch(prepareCatAction(params, formValues));
    case 'catExcessAmount':
      if (!formValues.user || !Object.hasOwnProperty.call(formValues.user, 'customerID')) {
        return Promise.reject('Missing customerID');
      }
      return getCatExcessAmount(formValues, dispatch);
    case 'createPortraitLead':
      return dispatch(portratiLeadAction(params, formValues));
    default:
      return Promise.resolve();
  }
};

/**
 * Calls an array of services. If this is a chain for the loader we update that as well.
 * @param  {array}    services     The services array.
 * @param  {Object}   serviceProps Props to use for this service.
 * @param  {Object}   updateLoader Reset loader.
 * @param  {Object}   formValues   The current values.
 * @param  {Function} dispatch     The dispatch function.
 */
export const callServices = (
  services,
  updateLoader,
  serviceProps,
  formValues,
  dispatch
) => {
  // First we need to filter out services we should not call.
  const servicesToCall = services.filter(service => {
    return !(service.forProduct && !formValues.basketList.includes(service.forProduct));
  });

  const { lastService, useSpinner } = serviceProps;

  // Create array to keep track of which services has been called.
  let finishedServices = 0;

  // Start spinner if needed.
  if (useSpinner) {
    dispatch(appLoadingAction(true));
  }

  // Loop the services to call.
  servicesToCall.forEach((service) => {
    const {
      serviceName, serviceParams, forProduct,
    } = service;
    callService(
      serviceName,
      serviceParams,
      forProduct,
      formValues,
      dispatch
    ).then(() => {
      // Check if we need to update the loader.
      if (updateLoader) {
        dispatch(userDataUpdateLoaderAction(0));
      }
      finishedServices++;
      if (finishedServices === servicesToCall.length) {
        if (lastService) {
          callService(
            lastService.serviceName,
            lastService.serviceParams,
            lastService.forProduct,
            formValues,
            dispatch
          ).then(() => {
            if (updateLoader) {
              dispatch(userDataUpdateLoaderAction(1));
            }
            // Stop spinner if needed.
            if (useSpinner) {
              dispatch(appLoadingAction(false));
            }
          });
        } else {
          if (useSpinner) {
            // Stop spinner if needed.
            dispatch(appLoadingAction(false));
          }
          if (updateLoader) {
            dispatch(userDataUpdateLoaderAction(1));
          }
        }
      }
    });
  });
};

/**
 * Get Policy ID.
 * @param  {Object} userData User data from state
 * @param  {string} product  Product to match
 * @param  {boolean} useFirst If we should get the first key from multiple calculation
 * @return {boolean|string}
 */
export const getPolicyID = (userData, product, useFirst = false) => {
  const { chosenPackage } = userData;
  if (userData[product] && userData[product].policyID) {
    return userData[product].policyID;
  }
  if (chosenPackage) {
    const { [product]: { [chosenPackage]: { policyID } } } = userData;
    return policyID;
  }
  if (useFirst && userData[product]) {
    const firstKey = Object.keys(userData[product])[0];
    const { [product]: { [firstKey]: { policyID } } } = userData;
    return policyID;
  }
  // If policyID is missing we fail.
  return false;
};
/**
 * Object Mapping.
 * @param  {Object} targetObj target object
 * @param  {Object} sourceObj  source object
 * * @return {Object}
 */
export const objectMapping = (targetObj, sourceObj) => {
  const resultObj = targetObj;
  Object.keys(sourceObj).forEach((key) => {
    resultObj[key] = sourceObj[key];
  });
  return resultObj;
};
/**
 * Get value based on Environment.
 * @param env
 * @param data
 * @return {*}
 */
export const getEnvValue = (env, data) => {
  if (typeof data === 'object') {
    return data[env] ? data[env] : data.default;
  }
  return data;
};


export const getRequestPreparePurchase = (type, state, formValues, params) => {
  const { userData } = state;
  const values = { ...userData, ...formValues };
  const policyID = getPolicyID(userData, type);
  const terminateExternalPartyCodeKey = `terminateExternalPartyCode-${type}`;
  let extraData = {
    terminateInsurance: false,
    terminateExternalPolicyNumber: '',
  };
  if (values[terminateExternalPartyCodeKey] || values.terminateExternalPartyCode) {
    extraData = {
      terminateInsurance: true,
      terminateExternalPartyCode:
        values[terminateExternalPartyCodeKey] ||
        values.terminateExternalPartyCode,
      terminateExternalPolicyNumber: 'Ved ikke',
    };
  }
  const mappedParams =
    params ?
      createParams(params, formValues, state, type, { ...extraData, policyID }) :
      {};
  const service = `${policyID}/preparePurchase`;

  return { service, mappedParams };
};


// WEBPACK FOOTER //
// ./src/utilities/api/common.js